<script lang="ts" setup>
import { ref, provide, computed, onMounted } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useRouter } from 'vue-router';
import DesktopLayout from './layouts/DesktopLayout.vue';
import MobileLayout from './layouts/MobileLayout.vue';

defineOptions({
    components: {
        'desktop-layout': DesktopLayout,
        'mobile-layout': MobileLayout
    }
});

const props = withDefaults(defineProps<{
    filter: FormType<unknown>;
    routeKey?: string;
    advanced?: boolean;
    settings?: Filters;
}>(), {
    routeKey: undefined,
    advanced: true,
    settings: null
});
const emit = defineEmits<{
    (e: 'init'): void;
    (e: 'change'): void;
}>();

const router = useRouter();

const loaded = ref(false);
const routeKey = computed(() => props.routeKey !== undefined ? props.routeKey : router.currentRoute.value.name.toString());
const dirty = ref<string[]>([]);
const visible = ref<string[]>([]);
const components = ref<{
    index: number;
    name: string;
    label: string;
    visible: boolean;
    instance: any;
}[]>([]);

function register(index: number, name: string, label: string, defaultVisible: boolean, instance: any)
{
    let enabled = true;

    if (props.settings)
    {
        enabled = props.settings.enabled?.includes(name);

        if (name in props.settings.visible)
            defaultVisible = props.settings.visible[name];

        if (name in props.settings.positions)
            index = props.settings.positions[name];
    }

    if (enabled)
    {
        components.value.push({
            name: name,
            label: label,
            visible: defaultVisible,
            index: index,
            instance: instance
        });

        if (defaultVisible || props.filter.dirty().includes(name))
        {
            visible.value.push(name);
        }
    }
}

function unregister(name: string)
{
    components.value.remove(p => p.name == name);
    visible.value.remove(p => p == name);
}

function change(name: string)
{
    if (name)
    {
        dirty.value.remove(p => p == name);
        dirty.value.push(name);
    }
}

function reset(name: string = null)
{
    dirty.value.remove(p => name ? p == name : true);

    components.value.forEach((c) =>
    {
        c.instance.exposed.clear(name || c.name);
    });

    change(name);
}

const apply = useDebounceFn(() =>
{
    emit('change');
},
100);

onMounted(() =>
{
    if (!loaded.value)
    {
        emit('init');
        loaded.value = true;
    }
});

provide('filter', props.filter);
provide('routeKey', routeKey);
provide('components', components);
provide('visible', visible);
provide('dirty', dirty);
provide('property', (name: string) => props.filter && name in props.filter ? props.filter[name] : null);
provide('register', register);
provide('unregister', unregister);
provide('change', change);
provide('reset', reset);
provide('apply', apply);
</script>

<template>
    <component :is="phone ? 'mobile-layout' : 'desktop-layout'" :advanced="advanced">
        <slot name="default" />
    </component>
</template>
